import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { TRANSLATIONS } from '../../translations';
import { useAuth } from '../hooks/useAuth';
import posthog from 'posthog-js';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: "en" | "pl";
  onRequestLogin?: () => void;
}

const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose, language, onRequestLogin }) => {
  useEffect(() => {
    if (isOpen && posthog) {
      posthog.capture("pricing_modal_opened", {
        language: language,
      });
    }
  }, [isOpen, language]);

  const { user } = useAuth();

  if (!isOpen) return null;

  const t = TRANSLATIONS[language];

  const handleUpgrade = async () => {
    const PRICE_ID = import.meta.env.VITE_YOUR_STRIPE_PRICE_ID as string | undefined;

    const userId = user?.id;
    if (!userId) {
      toast.error(t.upgradeNeedLogin);
      onRequestLogin?.();
      return;
    }

    if (!PRICE_ID) {
      console.error("Missing VITE_YOUR_STRIPE_PRICE_ID");
      toast.error(t.upgradeCheckoutFailed);
      return;
    }

    try {
      if (posthog) {
        posthog.capture("stripe_checkout_started", {
          priceId: PRICE_ID,
          language: language,
        });
      }

      const response = await fetch('/.netlify/functions/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId: PRICE_ID, userId: userId }),
      });

      let data: { url?: string; error?: string } = {};
      try {
        data = await response.json();
      } catch {
        toast.error(t.upgradeCheckoutFailed);
        return;
      }

      if (!response.ok || !data.url) {
        toast.error(data.error ?? t.upgradeCheckoutFailed);
        return;
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Error initiating Stripe checkout:', error);
      toast.error(t.upgradeCheckoutFailed);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl"
        >
          &times;
        </button>
        <h2 className="font-heading text-2xl font-bold mb-4 text-center">{t.pricingModalTitle}</h2>
        <div className="border border-indigo-600 rounded-lg p-4 mb-6">
          <h3 className="font-heading text-xl font-semibold mb-2">{t.planPro}</h3>
          <p className="font-sans text-gray-700 text-sm leading-relaxed mb-4">{t.pricingModalProDescription}</p>
          <button
            type="button"
            data-testid="pricing-upgrade-btn"
            onClick={handleUpgrade}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            {t.upgradeToProBtn}
          </button>
        </div>
        <p className="text-sm text-gray-500 text-center">{t.pricingFooterNote}</p>
      </div>
    </div>
  );
};

export default PricingModal;
